// 11章 タイル工事 — やさしい解説
// 原文は src/spec_index.json（PDFから自動生成）。このファイルは手書きで管理する。
// SVGは currentColor を使い、ライト/ダーク両テーマに追従させる。

import type { Commentary } from "../types";

const SVG_TILE_METHODS = `
<svg viewBox="0 0 660 270" role="img" aria-label="タイル張りの主な工法。セメントモルタル系は密着張りでヴィブラートを使いモルタルをタイル裏に回す、改良圧着張りは下地とタイル両方にモルタルを塗る。有機系接着剤張りは接着剤をくし目ごてで塗ってタイルを張る">
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <rect x="20"  y="60" width="300" height="180" rx="10"/>
    <rect x="340" y="60" width="300" height="180" rx="10"/>
  </g>
  <g fill="currentColor" font-size="13.5" font-weight="bold" text-anchor="middle">
    <text x="170" y="86">セメントモルタル張り（2節）</text>
    <text x="490" y="86">有機系接着剤張り（3節）</text>
  </g>
  <g fill="currentColor" font-size="12">
    <text x="36" y="116">・密着張り：張付けモルタルに</text>
    <text x="48" y="136">タイルを押し当て、ヴィブラートで</text>
    <text x="48" y="156">振動させ裏面全面に回す</text>
    <text x="36" y="182">・改良圧着張り：下地とタイル</text>
    <text x="48" y="202">両方にモルタルを塗って張る</text>
    <text x="36" y="226" opacity="0.8">・モザイク/マスク張り（ユニット）</text>
    <text x="356" y="116">・接着剤を金ごてで平たんに塗り</text>
    <text x="368" y="136">くし目ごてで60°でくし目を立てる</text>
    <text x="356" y="162">・内装＝JIS A 5548（F☆☆☆☆）</text>
    <text x="356" y="188">・外装＝JIS A 5557</text>
    <text x="368" y="208">（変成シリコーン樹脂系）</text>
    <text x="356" y="230" opacity="0.8">下地は十分乾燥・水湿ししない</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="20" y="40">共通のゴール：タイルに「有害な浮きがないこと」（11.1.2）。浮きは剥落＝人身事故につながる</text>
  </g>
</svg>`;

const SVG_VIBRATE = `
<svg viewBox="0 0 660 240" role="img" aria-label="密着張りの原理。張付けモルタルを下地に塗り、タイルを押し当ててタイル張り用振動機ヴィブラートで振動を与え、モルタルがタイル裏面全面に回り周辺からはみ出すまで動かす">
  <g fill="currentColor" opacity="0.3"><rect x="60" y="80" width="360" height="30"/></g>
  <g fill="currentColor" opacity="0.55"><rect x="90" y="110" width="90" height="14"/><rect x="200" y="110" width="90" height="14"/><rect x="310" y="110" width="90" height="14"/></g>
  <g fill="currentColor" opacity="0.85"><rect x="90" y="124" width="90" height="20"/><rect x="200" y="124" width="90" height="20"/><rect x="310" y="124" width="90" height="20"/></g>
  <g stroke="currentColor" stroke-width="4" stroke-linecap="round">
    <line x1="245" y1="60" x2="245" y2="105"/>
  </g>
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <path d="M225 55 q20 -14 40 0" opacity="0.7"/>
    <path d="M218 48 q27 -20 54 0" opacity="0.5"/>
  </g>
  <g fill="currentColor" font-size="12.5" font-weight="bold">
    <text x="270" y="55">ヴィブラート（振動機）</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.85">
    <text x="430" y="95">タイル</text>
    <text x="430" y="132">張付けモルタル</text>
    <text x="60" y="180">振動でモルタルがタイル裏面「全面」に回り、周辺からはみ出すまで動かす → 浮きを防ぐ</text>
    <text x="60" y="204">1回の塗付け面積は「触ると手に付く状態」のまま張り終える量（2㎡/人以内）</text>
    <text x="60" y="226" opacity="0.8">目地詰めは張付け後24時間以上経過し、モルタル硬化を見計らって行う</text>
  </g>
</svg>`;

const SVG_TILE_TEST = `
<svg viewBox="0 0 660 260" role="img" aria-label="タイルの施工後確認。全面を打診棒でたたいて浮きを探す打診と、油圧式試験機でタイルを引っ張る引張接着試験がある。合格は接着強度0.4ニュートン毎平方ミリ以上">
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <rect x="20"  y="50" width="300" height="170" rx="10"/>
    <rect x="340" y="50" width="300" height="170" rx="10"/>
  </g>
  <g fill="currentColor" font-size="13.5" font-weight="bold" text-anchor="middle">
    <text x="170" y="78">① 打診（全面）</text>
    <text x="490" y="78">② 引張接着試験（抜取り）</text>
  </g>
  <g fill="currentColor" opacity="0.5"><rect x="50" y="120" width="100" height="60"/></g>
  <g stroke="currentColor" stroke-width="3" stroke-linecap="round">
    <line x1="180" y1="100" x2="150" y2="150"/>
  </g>
  <g fill="currentColor"><circle cx="150" cy="150" r="6"/></g>
  <g fill="currentColor" font-size="11.5">
    <text x="46" y="200">たたいた音で浮きを判定</text>
    <text x="185" y="120">打診棒</text>
  </g>
  <g fill="currentColor" opacity="0.55"><rect x="420" y="150" width="60" height="30"/></g>
  <g stroke="currentColor" stroke-width="2"><line x1="450" y1="150" x2="450" y2="100"/></g>
  <g fill="currentColor"><polygon points="450,94 444,106 456,106"/></g>
  <g fill="currentColor" font-size="11.5">
    <text x="366" y="130">引っ張って強度を測定</text>
    <text x="366" y="205">100㎡ごと＋端数に1個以上</text>
    <text x="366" y="223">（全体で3個以上）</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.85">
    <text x="20" y="245">合格：セメントモルタル張りで0.4N/mm²以上かつ界面破壊率50%以下。浮きは張り直し（11.1.7）</text>
  </g>
</svg>`;

const SVG_EXP_JOINT_TILE = `
<svg viewBox="0 0 660 250" role="img" aria-label="タイルの伸縮調整目地の位置。外壁垂直方向は柱の両側や3〜4メートル程度ごと、水平方向は各階ごと打継ぎ目地の位置、床は4メートル以内ごとに設ける。目地は躯体と縁を切って設ける">
  <g fill="none" stroke="currentColor" stroke-width="2"><rect x="60" y="40" width="360" height="180"/></g>
  <g stroke="currentColor" stroke-width="2" stroke-dasharray="6 4" opacity="0.85">
    <line x1="150" y1="40" x2="150" y2="220"/>
    <line x1="270" y1="40" x2="270" y2="220"/>
    <line x1="390" y1="40" x2="390" y2="220"/>
    <line x1="60" y1="100" x2="420" y2="100"/>
    <line x1="60" y1="160" x2="420" y2="160"/>
  </g>
  <g fill="currentColor" font-size="12">
    <text x="440" y="70" font-weight="bold">伸縮調整目地の位置</text>
    <text x="440" y="98">垂直：柱の両側・3〜4ｍ程度</text>
    <text x="440" y="124">水平：各階ごと（打継ぎ位置）</text>
    <text x="440" y="150">床：縦横4ｍ以内ごと</text>
    <text x="440" y="180" opacity="0.85">（保護コンクリート上は3ｍ程度）</text>
    <text x="440" y="210" opacity="0.85">目地は躯体と縁を切り、</text>
    <text x="440" y="228" opacity="0.85">シーリングで仕上げる</text>
  </g>
  <g fill="currentColor" font-size="11.5" opacity="0.75">
    <text x="70" y="240">下地のひび割れ誘発目地・打継ぎ目地・構造スリット・他部材の取合いにも設ける</text>
  </g>
</svg>`;

const ch11: Record<string, Commentary> = {
  "11.1.1": {
    plainSummary:
      "この章はセラミックタイルを用いる内外装仕上げ工事のルール。1章とセットで適用する。",
    why: "タイルは丈夫で美しい反面、剥がれて落ちると人に当たる重大事故になります。だからこの章は「いかに浮き（剥離）を出さないか」と「浮きをどう発見するか」に多くの規定を割いています。",
    diagrams: [
      {
        svg: SVG_TILE_METHODS,
        caption: "タイル張りの主な工法（11章の全体像）",
      },
    ],
  },

  "11.1.2": {
    plainSummary:
      "ゴールは3つ。①所定の材料 ②タイルの仕上り面が所定の形状・寸法 ③タイルに有害な浮きがないこと。",
    glossary: [
      {
        term: "浮き",
        meaning:
          "タイルや下地モルタルが下地から剥離して隙間ができた状態。放置すると剥落する。",
      },
    ],
  },

  "11.1.3": {
    plainSummary:
      "伸縮調整目地・ひび割れ誘発目地の位置は特記がなければ表11.1.1（外壁垂直は柱両側・3〜4ｍ程度、水平は各階ごと、床は4ｍ以内ごと）。下地のひび割れ誘発目地・打継ぎ目地・構造スリット・他部材の取合い部にも設ける。入隅・建具枠回りにも設け、躯体と縁を切る。",
    why: "タイルもコンクリート躯体も温度で伸び縮みします。目地を入れずに全面をがっちり張ると、躯体の動きでタイルが押し合って浮き・剥落します。伸縮調整目地は、面を区切って動きを逃がす「逃げ場」です。躯体と縁を切るのが重要ポイントです。",
    points: [
      "目地位置は下地のひび割れ誘発目地・打継ぎと必ず合わせる",
      "目地は下地コンクリートまで達し、躯体と縁を切る",
    ],
    diagrams: [
      {
        svg: SVG_EXP_JOINT_TILE,
        caption: "伸縮調整目地の位置（表11.1.1）",
      },
    ],
  },

  "11.1.4": {
    plainSummary: "タイルの見本焼・試験張りの適用は特記による。",
    glossary: [
      {
        term: "見本焼",
        meaning: "色調・寸法を確認するために事前に焼いたタイルの見本。",
      },
    ],
  },

  "11.1.5": {
    plainSummary:
      "外装タイル張りで降雨・降雪・強風が予想される場合等は施工しない。施工中・施工後の気温が5℃以下になると予想される場合は原則施工しない。",
    why: "低温だとモルタルや接着剤の硬化・接着が不十分になり、後の剥落につながります。「5℃以下は原則やらない」はタイルだけでなく左官・塗装にも共通する寒中施工の目安です。",
  },

  "11.1.6": {
    plainSummary:
      "タイル張りに先立ち、下地モルタル・下地調整塗材について、硬化不良・はく離・ひび割れ・浮きがないこと、汚れ・レイタンス等の有害な付着物がないこと、下地精度が確保されていることを確認し、不具合は補修する。",
    why: "タイルの浮きの原因の多くは下地にあります。下地が浮いていればタイルも一緒に落ちます。「張る前に下地を打診・確認」が剥落防止の第一歩です。",
  },

  "11.1.7": {
    plainSummary:
      "施工後は①外観確認（色調・不陸・割れ・目地の不ぞろい等を目視）②打診（屋外・吹抜け部は全面を打診し、浮き・ひび割れは張り直し）③引張接着試験（屋外・吹抜け部の壁タイルは油圧式試験機で測定、100㎡ごと＋端数に1個以上・全体3個以上、合格はセメントモルタル張りで0.4N/mm²以上かつ界面破壊率50%以下）。",
    why: "タイルの品質は「張った後に確かめる」のが基本。打診で音の違いから浮きを全数チェックし、引張接着試験で接着強度を数値で確認します。0.4N/mm²という合格基準と、100㎡ごと・全体3個以上という試験頻度は実務・試験の頻出数値です。剥落は人命に関わるため、この確認は省略できません。",
    points: [
      "打診は屋外・吹抜け部の「全面」（抜取りではない）",
      "引張接着試験：0.4N/mm²以上・100㎡ごと・全体3個以上",
      "浮きが見つかったら補修ではなく「張り直し」",
    ],
    diagrams: [
      {
        svg: SVG_TILE_TEST,
        caption: "打診と引張接着試験（11.1.7）",
      },
    ],
  },

  "11.2.1": {
    plainSummary:
      "この節は工事現場でセメントモルタルによる後張りでタイル張り仕上げを行う工事に適用する（300角超のタイルの床張りは除く）。",
    glossary: [
      {
        term: "後張り工法",
        meaning:
          "躯体完成後にタイルを張る工法。型枠にタイルを先付けする「先付け工法」の対語。",
      },
    ],
  },

  "11.2.2": {
    plainSummary:
      "タイルはJIS A 5209により形状・寸法・耐凍害性・耐滑り性等は特記。役物（角・縁のタイル）の適用は特記で、内装タイルは面取りしたものを使う。まぐさ・窓台の役物は水切りのよい形状。",
    glossary: [
      {
        term: "役物（やくもの）",
        meaning:
          "出隅・入隅・見切りなど特殊な位置に使う成形タイル。平物（一般部のタイル）の対語。",
      },
      {
        term: "耐凍害性",
        meaning:
          "凍結融解の繰り返しで割れないタイルの性質。寒冷地の外装で重要。",
      },
    ],
  },

  "11.2.3": {
    plainSummary:
      "張付けモルタルの材料。セメントはポルトランドセメント、細骨材の最大粒径は一般2.5mm・ユニットタイル1.2mm・目地モルタル0.6mm。保水剤（メチルセルロース等）、ポリマーディスパージョン、吸水調整材、既調合モルタル・目地材を使う。",
    glossary: [
      {
        term: "保水剤",
        meaning:
          "モルタルの水分がタイル・下地に急に吸われるのを抑え、接着を高める混和剤。",
      },
    ],
  },

  "11.2.4": {
    plainSummary:
      "引金物はなましステンレス鋼線（SUS304）径0.6mm以上・働き長さ200mm程度。伸縮調整目地等のシーリング材は9章7節による。",
  },

  "11.2.5": {
    plainSummary:
      "張付けモルタルの調合（容積比）は工法・部位ごとに表11.2.2による（密着張り＝セメント1:砂1〜2等）。練混ぜは原則機械練り、1回の練混ぜ量は60分以内に張り終える量とする。",
    why: "「60分以内に使い切る量だけ練る」のは、時間が経ったモルタルは硬化が始まり接着力が落ちるためです。作り置きは剥落の原因になります。",
  },

  "11.2.6": {
    plainSummary:
      "下地は目荒し工法・MCR工法等で処理し水湿しまたは吸水調整材を塗布。床タイルは張付けモルタルを2層に分けて塗り（1層目はこて圧、塗付け面積2㎡/人以内）水糸で通りよく張る。壁タイルは工法ごとの塗厚（密着張り5〜8mm等）で、密着張りはヴィブラートで振動を与えモルタルが裏面全面に回りはみ出すまで移動させる。目地詰めは張付け後24時間以上経過し硬化を見計らって行う。",
    why: "この項がタイル張りの核心です。密着張りの品質はヴィブラート（振動機）の使い方で決まります。振動でモルタルをタイル裏面の隅々まで行き渡らせ、周辺からはみ出すのを確認する。これで裏に空気（＝浮き）が残りません。「2㎡/人以内」は、モルタルが乾く前に張り終えられる範囲の目安です。",
    points: [
      "密着張りはヴィブラートで「周辺からはみ出すまで」振動",
      "張付けモルタルは触ると手に付く状態のうちに張る（オープンタイム管理）",
      "目地詰めは24時間以上経過後（早すぎると接着不良）",
    ],
    diagrams: [
      {
        svg: SVG_VIBRATE,
        caption: "密着張りとヴィブラート（11.2.6）",
      },
    ],
  },

  "11.2.7": {
    plainSummary:
      "施工中・モルタル未硬化のうちは振動・衝撃を与えない。床タイル張り後3日間は歩行しない（やむを得ず歩く場合は道板養生）。清掃は水洗いを基本とし、汚れが著しい場合は承諾を受けて酸類を使う（酸洗い前後に水洗い・金物は養生）。",
    points: [
      "張ったばかりの床タイルは3日間歩かない",
      "酸洗いは最後の手段（金物養生と十分な水洗いが必須）",
    ],
  },

  "11.3.1": {
    plainSummary:
      "この節は工事現場で有機系接着剤による後張りでタイル張り仕上げを行う工事に適用する。",
    why: "有機系接着剤張りは、モルタルより軽く速く、接着力も安定するため近年増えている工法です。特に内装で主流になっています。",
  },

  "11.3.2": {
    plainSummary:
      "タイルはJIS A 5209により特記。役物の適用は特記で、内装タイルは面取りしたものを使う。まぐさ・窓台の役物は水切りのよい形状（小口平以上をまぐさ・ひさし先端下部に使う場合はL形）。",
  },

  "11.3.3": {
    plainSummary:
      "内装用接着剤はJIS A 5548によりタイプⅠ（長期に水の影響）・タイプⅡ（間欠的に水の影響）を施工箇所に応じて使う（ホルムアルデヒド放散量は特記がなければF☆☆☆☆）。屋外用はJIS A 5557の一液反応硬化形変成シリコーン樹脂系。",
    why: "水掛りの箇所には耐水性の高いタイプの接着剤を使う、という使い分けが重要。浴室など水がかかる場所に一般用を使うと剥がれます。屋外は耐候性が要るため変成シリコーン系に限定されます。",
    glossary: [
      {
        term: "F☆☆☆☆（エフフォースター）",
        meaning:
          "ホルムアルデヒド放散量が最も少ない等級。シックハウス対策の基準。",
      },
    ],
  },

  "11.3.4": {
    plainSummary:
      "シーリング材はJIS A 5758により、特記がなければ打継ぎ目地・ひび割れ誘発目地はポリウレタン系、伸縮調整目地その他は変成シリコーン系。外装タイル接着剤張りのシーリングは接着剤による汚染が出ないことを確認する。",
  },

  "11.3.5": {
    plainSummary:
      "下地は目荒し・MCR工法等で処理し、タイル張付け時は下地を十分乾燥させる（水湿し・吸水調整材の塗布はしない）。接着剤は金ごてで平たんに塗った後、くし目ごてで壁面に60°の角度でくし目を立てる（裏あし方向とくし目方向を平行にしない）。手でもみ込んだ後たたき押えるか振動工具で加振して張る。目地詰めは接着剤の硬化を確認後に行う。",
    why: "接着剤張りはモルタル張りと逆で「下地を濡らさない」のが鉄則。水分があると接着剤が硬化不良を起こします。くし目を60°で立てるのは、接着剤の量と空気の逃げ道を最適にするため。裏あしとくし目を平行にしないのは、接着剤が裏あしの溝に確実に入り込むようにするためです。",
    points: [
      "接着剤張りは下地を乾燥させる（水湿ししない）＝モルタル張りと真逆",
      "くし目ごては壁面に60°で立てる",
      "裏あし方向とくし目方向を平行にしない",
    ],
    glossary: [
      {
        term: "くし目ごて",
        meaning:
          "刃が櫛状のこて。接着剤を一定の高さの筋状に塗り、接着量と空気逃げを均一にする。",
      },
      {
        term: "裏あし",
        meaning:
          "タイル裏面の凹凸（あり足）。モルタルや接着剤が食い込んで接着力を高める。",
      },
    ],
  },

  "11.3.6": {
    plainSummary:
      "施工中・接着剤未硬化のうちは振動・衝撃を与えない。清掃は水洗いを基本とし、接着剤がタイル表面に付着して硬化した場合は汚れ除去用発泡樹脂・砂消しゴム等で削り取る（平滑な内装タイルは硬化前に溶剤でふき取る）。",
  },
};

export default ch11;
